import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useReducer,
} from 'react';

import { Messages } from '../../components/Message/types/messages';
import { AppEndpoints, EndpointConfigOptions } from '../../types/app';
import { appContextReducer } from './appContextReducer';
import { initialAppContextState } from './initialAppContextState';
import { AppContextDispatcher, AppContextState } from './types';

export type AppContextValue = readonly [
  AppContextState,
  AppContextDispatcher,
  AppEndpoints,
];

/**
 * App Context
 */
export const AppContext = createContext<AppContextValue>([
  initialAppContextState,
  () => {},
  {
    initChat: () => ({}) as Promise<Messages>,
    resumeChat: () => ({}) as Promise<Messages>,
    sendMessage: () => ({}) as Promise<Messages>,
    handleMessageCopied: () => new Promise(() => {}),
    handleMessageReaction: () => new Promise(() => {}),
  },
]);

/**
 * App Context Provider
 */
export const AppContextProvider: FC<{
  children: ReactNode;
  config: EndpointConfigOptions;
  endpoints: AppEndpoints;
}> = ({ children, config, endpoints }) => {
  const [state, _dispatch] = useReducer(appContextReducer, {
    ...initialAppContextState,
    ...config,
  });

  const dispatch = useCallback<AppContextDispatcher>((action) => {
    _dispatch(action);
  }, []);

  const contextValue = useMemo<AppContextValue>(
    () => [state, dispatch, endpoints],
    [dispatch, endpoints, state],
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};
